import type { PageState } from './types'
import s from './Pager.module.scss'

/**
 * Das Blättern.
 *
 * Die Fensterlogik ist aus POC v1 übernommen: erste Seite, ein Nachbar links
 * und rechts, letzte Seite — Lücken als `…`. Bei 25 Seiten stehen so fünf
 * Knöpfe da statt 25, und die Enden bleiben mit einem Klick erreichbar.
 *
 * Ohne bekannte Gesamtzahl gibt es keine letzte Seite und damit keine
 * Zahlenreihe: Dann bleiben Vor und Zurück, und `hasMore` entscheidet, ob es
 * weitergeht.
 */
export function Pager({ page, onPage }: { page: PageState; onPage: (page: number) => void }) {
  const pages = page.total == null ? null : Math.max(1, Math.ceil(page.total / page.size))
  const current = page.page
  const hasNext = pages == null ? (page.hasMore ?? false) : current < pages

  if (pages != null && pages <= 1) return null

  const window_ = pages == null ? [] : [
    ...new Set([1, current - 1, current, current + 1, pages]),
  ].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b)

  return (
    <nav className={s.pager} aria-label="Seiten">
      <button type="button" className={s.button} disabled={current <= 1}
              onClick={() => onPage(current - 1)} aria-label="vorige Seite">‹</button>

      <span className={s.numbers}>
        {window_.map((p, index) => (
          <span key={p} className={s.pair}>
            {index > 0 && p - window_[index - 1] > 1 && <span className={s.gap}>…</span>}
            <button type="button" className={s.button} data-current={p === current}
                    aria-current={p === current ? 'page' : undefined}
                    onClick={() => onPage(p)}>{p}</button>
          </span>
        ))}
      </span>

      {/* Am Handy statt der Zahlenreihe. */}
      <span className={s.current}>
        {pages == null ? `Seite ${current}` : `${current} / ${pages}`}
      </span>

      <button type="button" className={s.button} disabled={!hasNext}
              onClick={() => onPage(current + 1)} aria-label="nächste Seite">›</button>
    </nav>
  )
}
